import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faCog, 
  faRobot, 
  faFileCode, 
  faTools, 
  faArrowLeft,
  faExclamationTriangle,
  faSpinner
} from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../../context/AuthContext';
import PromptsTab from './PromptsTab';
import ToolsTab from './ToolsTab';
import GeneralTab from './GeneralTab';
import AdvancedTab from './AdvancedTab';
import './AgentSettings.css';

const AgentSettingsPage = () => {
  const { agentId } = useParams();
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [activeTab, setActiveTab] = useState('general');
  const [agentOverview, setAgentOverview] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Check admin access
  useEffect(() => {
    if (user && user.role !== 'admin') {
      navigate('/dashboard');
      return;
    }
  }, [user, navigate]);

  // Fetch agent overview
  useEffect(() => {
    const fetchAgentOverview = async () => {
      if (!token || !user) return;

      try {
        setIsLoading(true);
        const response = await fetch(`/api/agents/${agentId}/settings/overview`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          const data = await response.json();
          setAgentOverview(data.agent);
        } else {
          throw new Error(`Failed to fetch agent overview: ${response.status}`);
        }
      } catch (err) {
        setError(`Failed to load agent settings: ${err.message}`);
        console.error('Error fetching agent overview:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchAgentOverview();
  }, [agentId, token, user]);

  const tabs = [
    {
      id: 'general',
      name: 'General',
      icon: faRobot,
      description: 'Basic agent information'
    },
    {
      id: 'prompts',
      name: 'Prompts',
      icon: faFileCode,
      description: 'Manage AI prompts',
      count: agentOverview?.prompt_count
    },
    // {
    //   id: 'tools',
    //   name: 'Tools',
    //   icon: faTools,
    //   description: 'Configure tool definitions',
    //   count: agentOverview?.tool_count
    // },
    {
      id: 'advanced',
      name: 'Advanced',
      icon: faCog,
      description: 'Export/Import & raw config'
    }
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case 'general':
        return <GeneralTab agentId={agentId} agentOverview={agentOverview} />;
      case 'prompts':
        return <PromptsTab agentId={agentId} />;
      // case 'tools':
      //   return <ToolsTab agentId={agentId} />;
      case 'advanced':
        return <AdvancedTab agentId={agentId} agentOverview={agentOverview} />;
      default:
        return <GeneralTab agentId={agentId} agentOverview={agentOverview} />;
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="agent-settings-loading">
        <div className="loading-spinner">
          <FontAwesomeIcon icon={faSpinner} spin size="2x" />
        </div>
        <p>Loading agent settings...</p>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="agent-settings-error">
        <FontAwesomeIcon icon={faExclamationTriangle} size="2x" />
        <h3>Error Loading Agent Settings</h3>
        <p>{error}</p>
        <button 
          className="back-button"
          onClick={() => navigate('/dashboard')}
        >
          <FontAwesomeIcon icon={faArrowLeft} />
          Back to Dashboard
        </button>
      </div>
    );
  }

  return (
    <div className="agent-settings-page">
      {/* Header */}
      <div className="agent-settings-header">
        <div className="header-nav">
          <button 
            className="back-button"
            onClick={() => navigate('/dashboard')}
          >
            <FontAwesomeIcon icon={faArrowLeft} />
            Back
          </button>
          <div className="breadcrumb">
            <span>Dashboard</span>
            <span>/</span>
            <span>Agents</span>
            <span>/</span>
            <span className="current">{agentOverview?.agent_name || 'Settings'}</span>
          </div>
        </div>
        
        <div className="agent-header-info">
          <div className="agent-icon">
            <FontAwesomeIcon icon={faRobot} />
          </div>
          <div className="agent-details">
            <h1>{agentOverview?.agent_name}</h1>
            <p>{agentOverview?.description}</p>
            <div className="agent-meta">
              <span className="version">v{agentOverview?.version}</span>
              <span className={`status ${agentOverview?.is_active ? 'active' : 'inactive'}`}>
                {agentOverview?.is_active ? 'Active' : 'Inactive'}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="agent-settings-tabs">
        <div className="tab-nav">
          {tabs.map(tab => (
            <button
              key={tab.id}
              className={`tab-button ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              <FontAwesomeIcon icon={tab.icon} />
              <span className="tab-name">{tab.name}</span>
              {tab.count !== undefined && (
                <span className="tab-count">{tab.count}</span>
              )}
            </button>
          ))}
        </div>

        <div className="tab-description">
          {tabs.find(tab => tab.id === activeTab)?.description}
        </div>
      </div>

      {/* Tab Content */}
      <div className="agent-settings-content">
        {renderTabContent()}
      </div>
    </div>
  );
};

export default AgentSettingsPage;